import React, { useEffect, useMemo, useState } from "react";
import { usePages } from "../../Contexts/InvoicePages";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Toolbar as MUIToolbar } from "@mui/material";
import { DraftsOutlined, MailOutline } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import _ from "lodash";

import { BACKEND_URL } from "../../../helpers/variables";
import { blue, green, yellow } from "@mui/material/colors";

import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";

import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { RenderThumbnailItemProps } from "@react-pdf-viewer/thumbnail";


const TAB_DIMENSION = 40;
const defaultStyles = {
  position: "absolute",
  border: "1px solid red",
  display: "none",
  pointerEvents: "none",
  boxSizing: "border-box",
  zIndex: 1100,
};

const FilePanel = ({ bill, file, email, invoice }) => {
  const page_groups = file["bills"]
    .map((i) => i["page_numbers"] || [])
    .filter((arr) => arr.length > 0);

  const { file_table_id, bill_status, page_numbers = [] } = bill;
  
  const url = invoice["preassigned_url"]

  const [styles, setStyles] = useState(defaultStyles);
  const [boundingBoxes, setBoundingBoxes] = useState([]);
  const [openMailBody, setMailBody] = useState(false);

  const { pages, updatePages, setPages, setFileLoadingFailed, setOpenForm } =
    usePages();
  const {
    checked_pages,
    blocked_pages,
    split_pages,
    invoice_pages,
    no_of_pages,
  } = pages;

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage, CurrentPageLabel } = pageNavigationPluginInstance;

  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;

  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;

  /**
   *
   * @param {import("@react-pdf-viewer/core").DocumentLoadEvent} param0
   */
  async function onDocumentLoad({ doc: { numPages } }) {
    let no_of_pages = Array.from({ length: numPages }, (_, k) => k + 1);
    if (![-1, -3].includes(bill_status)) fetchOCR_Response();

    const pageProperties = await getPageProperties();

    setPages((prev) => ({
      ...prev,
      no_of_pages,
      split_pages: pageProperties.splitted_pages,
      blocked_pages: pageProperties.locked_pages,
      checked_pages: page_numbers,
      invoice_pages: _.difference(pageProperties.invoice_pages, page_numbers),
    }));

    const allGrouped = new Set(page_groups.flat());
    const missed = no_of_pages.filter((p) => !allGrouped.has(p));
    if (missed.length) page_groups.push(missed);
    page_groups.sort((a, b) => a[0] - b[0]);
  }

  async function fetchOCR_Response() {
    let res = await fetch(
      `${BACKEND_URL}/get_ocr_response?file_table_id=${bill["file_table_id"]}`,
    );
    let data = await res.json();
    setBoundingBoxes(data);
  }

  async function getPageProperties() {
    const res = await fetch(
      `${BACKEND_URL}/page_properties?file_table_id=${file_table_id}`,
    );
    let data = await res.json();
    return data;
  }

  const getBoundingBox = (e, pageNumber) => {
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let page = boundingBoxes.find((i) => i["page_number"] === pageNumber) || {
      page_blocks: [],
    };

    return page["page_blocks"].find(
      (box) =>
        x >= box.Left * rect.width &&
        x <= (box.Left + box.Width) * rect.width &&
        y >= box.Top * rect.height &&
        y <= (box.Top + box.Height) * rect.height,
    );
  };

  function onMouseMoveHandler(e, pageIndex) {
    let rect = e.target.getBoundingClientRect();
    let boundingBox = getBoundingBox(e, pageIndex);

    if (boundingBox) {
      setStyles((prev) => ({
        ...prev,
        display: "block",
        width: `${boundingBox.Width * rect.width}px`,
        height: `${boundingBox.Height * rect.height}px`,
        top: `${boundingBox.Top * rect.height}px`,
        left: `${boundingBox.Left * rect.width}px`,
      }));
    } else {
      setStyles(defaultStyles);
    }
  }

  function copyTextFromPDf(e, pageIndex) {
    let boundingBox = getBoundingBox(e, pageIndex);
    if (boundingBox) {
      let text = boundingBox["Text"];
      navigator.clipboard.writeText(text);
      // enqueueSnackbar(`"${text}" copied to clipboard`, { variant: "info" });
      let message = { source: "nimbleio", text };
      window.parent.postMessage(message, "*");
    }
  }

  function updateCheckedPages(page) {
    setOpenForm(false);
    let newCheckedPages;
    if (checked_pages.includes(page)) {
      newCheckedPages = checked_pages.filter((i) => i !== page);
    } else {
      newCheckedPages = [...checked_pages, page];
    }
    updatePages("checked_pages", newCheckedPages);
  }

  /**
   *
   * @param {import("@react-pdf-viewer/core").RenderPageProps} props
   * @returns
   */
  function PageRender(props) {
    let pageNumber = Number(props["pageIndex"]) + 1;
    return (
      <>
        {props.canvasLayer.children}
        {props.textLayer.children}

        <Box
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
            cursor: "pointer",
          }}
          onMouseMove={(e) => onMouseMoveHandler(e, pageNumber)}
          onDoubleClick={(e) => copyTextFromPDf(e, pageNumber)}
        />
        <Box style={styles} />
      </>
    );
  }

  const tabFontColor = (page) =>
    checked_pages.includes(page) ||
      blocked_pages.includes(page) ||
      split_pages.includes(page) ||
      invoice_pages.includes(page)
      ? "white"
      : "inherit";

  const tabBorderColor = (page) =>
    checked_pages.includes(page) ||
      blocked_pages.includes(page) ||
      split_pages.includes(page) ||
      invoice_pages.includes(page)
      ? undefined
      : "divider";

  const tabColor = (page) =>
    checked_pages.includes(page)
      ? yellow[500]
      : blocked_pages.includes(page)
        ? green[500]
        : invoice_pages.includes(page) || split_pages.includes(page)
          ? blue[500]
          : undefined;

  function isAllPagesSelected() {
    let all_pages = _.union(checked_pages, blocked_pages, split_pages);
    return _.isEqual(_.sortBy(all_pages), _.sortBy(no_of_pages));
  }

  function unmodifiedPages() {
    const combined = _.union(blocked_pages, split_pages);
    return _.difference(no_of_pages, combined);
  }

  /**
   *
   * @param {import("react").ChangeEvent} e
   */
  function checkAllPages(e) {
    setOpenForm(false);
    let newCheckedPages = unmodifiedPages();

    let checked = e.target.checked;
    if (checked) {
      updatePages("checked_pages", newCheckedPages);
    } else {
      updatePages("checked_pages", []);
    }
  }

  return (
    <Stack
      height={"100%"}
      border={1}
      borderColor={"divider"}
      overflow={"hidden"}
    >
      <MUIToolbar variant="dense" sx={{ bgcolor: blue[50], gap: 1 }}>
        <Toolbar>
          {({ Zoom, Download, Rotate, CurrentPageLabel }) => (
            <>
              <CurrentPageLabel>
                {({ currentPage }) => (
                  <Tabs
                    sx={{
                      flexGrow: 1,
                      alignItems: "center",
                    }}
                    value={currentPage}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{
                      style: { display: "none" },
                    }}
                    TabScrollButtonProps={{
                      style: { height: 48 },
                    }}
                  >
                    {page_groups.map((group, groupIndex) =>
                      group.map((page, index) => {
                        const isFirstInGroup = index === 0;
                        const isLastInGroup = index === group.length - 1;

                        return (
                          <Tab
                            key={index}
                            label={page}
                            sx={{
                              mr: isLastInGroup ? 2 : 0,
                              borderTopLeftRadius: isFirstInGroup ? "50%" : 0,
                              borderBottomLeftRadius: isFirstInGroup
                                ? "50%"
                                : 0,
                              borderTopRightRadius: isLastInGroup ? "50%" : 0,
                              borderBottomRightRadius: isLastInGroup
                                ? "50%"
                                : 0,

                              minHeight: TAB_DIMENSION,
                              minWidth: TAB_DIMENSION,
                              maxWidth: TAB_DIMENSION,
                              maxHeight: TAB_DIMENSION,

                              color: tabFontColor(page),
                              bgcolor: tabColor(page),

                              "&.Mui-selected": {
                                color: tabFontColor(page),
                              },
                            }}
                            onClick={() => jumpToPage(page - 1)}
                          />
                        );
                      }),
                    )}
                  </Tabs>
                )}
              </CurrentPageLabel>

              <Box flexGrow={1} />
              <Zoom />
              <Rotate />
              <Download />
              <IconButton onClick={() => setMailBody((prev) => !prev)}>
                {openMailBody ? <DraftsOutlined /> : <MailOutline />}
              </IconButton>
              <Checkbox
                disabled={unmodifiedPages().length === 0}
                checked={isAllPagesSelected()}
                onChange={(e) => checkAllPages(e)}
              />
            </>
          )}
        </Toolbar>
      </MUIToolbar>

      {openMailBody && (
        <Stack
          p={1}
          border={1}
          borderColor={"divider"}
          minHeight={8 * 32}
          maxHeight={8 * 48}
          overflow={"auto"}
          gap={1}
        >
          <Typography variant="body2">From: {email["from"]}</Typography>
          <Typography variant="body2">To: {email["to"]}</Typography>
          <Typography variant="body2">Subject: {email["subject"]}</Typography>
          <Box dangerouslySetInnerHTML={{ __html: email["body"] }} />
        </Stack>
      )}

      <Stack direction={"row"} flexGrow={1} overflow={"auto"}>
        <Thumbnails
          renderThumbnailItem={({ pageIndex, currentPage }) =>
            pageIndex === 0 && (
              <FormControlLabel
                disabled={[blocked_pages, split_pages].some((arr) =>
                  arr.includes(currentPage + 1),
                )}
                checked={checked_pages.includes(currentPage + 1)}
                control={
                  <Checkbox
                    onClick={() => updateCheckedPages(currentPage + 1)}
                  />
                }
                label={currentPage + 1}
                labelPlacement="top"
                sx={{ mt: 2 }}
              />
            )
          }
        />
        <Divider flexItem orientation="vertical" />
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
          <Viewer
            onDocumentLoad={(e) => onDocumentLoad(e)}
            fileUrl={url}
            plugins={[
              toolbarPluginInstance,
              pageNavigationPluginInstance,
              thumbnailPluginInstance,
            ]}
            renderPage={PageRender}
            renderError={({ message, name }) => {
              enqueueSnackbar(message, { variant: "error" });
              setFileLoadingFailed(true);
            }}
            initialPage={(page_numbers[0] || 1) - 1}
          />
        </Worker>
      </Stack>
    </Stack>
  );
};

export default FilePanel;
